import { Injectable, computed, signal } from '@angular/core';
import { environment } from '../../../environments/environment';
import { REQUIRED_RUNTIME_CONFIG_KEYS, RuntimeConfig } from './runtime-config.types';

/**
 * Loads deployment-coupled configuration from `/assets/config.json` at
 * application boot and exposes it to the rest of the codebase as signals.
 *
 * Wired via `APP_INITIALIZER` in `app.config.ts` — consumers can safely
 * read the signals synchronously inside field initializers, because
 * Angular does not construct root services until after all initializers
 * have resolved.
 *
 * Uses `fetch` (not `HttpClient`) deliberately: this call runs *before*
 * the interceptor pipeline exists, and keeping it dependency-free means
 * there is no risk of a circular DI chain through an interceptor that
 * itself depends on runtime config.
 *
 * See `docs/deployment/runtime-config.md` for the deployment model.
 */
@Injectable({ providedIn: 'root' })
export class RuntimeConfigService {
  private readonly state = signal<RuntimeConfig | null>(null);
  // A field (not an inline `environment.production`) so a spec can flip it.
  private readonly isProductionBuild: boolean = environment.production;

  readonly apiUrl = computed(() => this.requireLoaded().apiUrl);
  readonly apiUrlHub = computed(() => this.requireLoaded().apiUrlHub);
  readonly apiVersion = computed(() => this.requireLoaded().apiVersion);
  readonly devAuthPublicKey = computed(() => this.requireLoaded().devAuth.publicKey);
  readonly authAutocompleteEnabled = computed(() => this.requireLoaded().authAutocompleteEnabled);
  readonly cacheDebug = computed(() => this.requireLoaded().cacheDebug);
  /** Shows Retry on the filing load-failure panel. See `RuntimeConfig.filingLoadRetryEnabled`. */
  readonly filingLoadRetryEnabled = computed(() => this.requireLoaded().filingLoadRetryEnabled);
  /**
   * REFACTORED-DIAGNOSTIC-CODES-FLAG — TEMPORARY. `true` shows the `MBRS-…`
   * codes in load-failure toasts; `false` (production) restores the previous
   * generic copy. See `RuntimeConfig.refactoredDiagnosticCodesEnabled` for the
   * removal plan.
   */
  readonly refactoredDiagnosticCodesEnabled = computed(
    () => this.requireLoaded().refactoredDiagnosticCodesEnabled,
  );
  readonly crossTabSyncDebug = computed(() => this.requireLoaded().crossTabSyncDebug);
  readonly crossSheetCalcDebug = computed(() => this.requireLoaded().crossSheetCalcDebug);
  /**
   * Temporary diagnostic flag — gates the `seance` liveness-probe harness in
   * non-production builds. See `RuntimeConfig.livenessProbeDebug`.
   */
  readonly livenessProbeDebug = computed(() => this.requireLoaded().livenessProbeDebug);
  readonly fieldNoteTooltipsEnabled = computed(() => this.requireLoaded().fieldNoteTooltipsEnabled);
  readonly mandatoryMarkersEnabled = computed(() => this.requireLoaded().mandatoryMarkersEnabled);
  /**
   * Temporary QA flag — allows Download on Review / Failed conversions in the
   * XBRL files listing. See `RuntimeConfig.nonSuccessDownloadEnabled`.
   */
  readonly nonSuccessDownloadEnabled = computed(
    () => this.requireLoaded().nonSuccessDownloadEnabled,
  );
  /**
   * Gates the conversion-engine picker AND its backing fetch. See
   * `RuntimeConfig.enginePickerEnabled`.
   */
  readonly enginePickerEnabled = computed(() => this.requireLoaded().enginePickerEnabled);
  /**
   * Forces the Failed render of the post-reset status page. See
   * `RuntimeConfig.forceResetPasswordFailure`. Always `false` in production
   * builds: `validate()` already refuses a `true` there, and this guard keeps
   * the flag off even if that check is ever loosened.
   */
  readonly forceResetPasswordFailure = computed(
    () => !this.isProductionBuild && this.requireLoaded().forceResetPasswordFailure,
  );
  /**
   * Enables the simulate-server-failure QA switch. See
   * `RuntimeConfig.simulateFailureEnabled`. Always `false` in production builds.
   */
  readonly simulateFailureEnabled = computed(
    () => !this.isProductionBuild && this.requireLoaded().simulateFailureEnabled,
  );
  /** Rows per page the XBRL files listing requests from the backend. */
  readonly xbrlFilesPageSize = computed(() => this.requireLoaded().listingPageSize.xbrlFiles);
  /** Rows per page the user-management listing requests from the backend. */
  readonly userManagementPageSize = computed(
    () => this.requireLoaded().listingPageSize.userManagement,
  );

  /**
   * Fetch and validate the runtime config. Called exactly once, by the
   * `APP_INITIALIZER` factory in `app.config.ts`. Throws on any failure
   * so Angular aborts bootstrap and the boot-error page is shown.
   */
  async load(): Promise<void> {
    const candidates = [
      '/assets/config.json',
    ];

    const attempts: string[] = [];
    let lastError: unknown;

    for (const path of candidates) {
      try {
        const response = await fetch(path, { cache: 'no-store' });
        if (!response.ok) {
          attempts.push(`${path} -> HTTP ${response.status}`);
          continue;
        }
        const raw = await response.json();
        const validated = this.validate(raw, path);
        this.state.set(validated);
        if (validated.cacheDebug) {
          const headerSnapshot: Record<string, string | null> = {};
          for (const header of ['cache-control', 'age', 'etag', 'last-modified', 'date', 'x-cache']) {
            headerSnapshot[header] = response.headers.get(header);
          }
          // eslint-disable-next-line no-console
          console.debug('[cache-debug] config loaded', {
            path,
            fetchedAt: new Date().toISOString(),
            headers: headerSnapshot,
            config: validated,
          });
        }
        return;
      } catch (err) {
        attempts.push(`${path} -> ${err instanceof Error ? err.message : String(err)}`);
        lastError = err;
      }
    }

    throw new Error(
      `RuntimeConfigService: no config file reachable. Attempts: ${attempts.join('; ')}`
      + (lastError ? ` (last error: ${lastError})` : ''),
    );
  }

  private requireLoaded(): RuntimeConfig {
    const value = this.state();
    if (value === null) {
      throw new Error(
        'RuntimeConfigService accessed before load() resolved. This usually means a service '
        + 'read config outside of Angular DI (e.g. at module-load time) — move the read into '
        + 'a method or field initializer inside an @Injectable.',
      );
    }
    return value;
  }

  private validate(raw: unknown, source: string): RuntimeConfig {
    const errors: string[] = [];

    if (raw === null || typeof raw !== 'object' || Array.isArray(raw)) {
      throw new Error(`RuntimeConfigService: ${source} did not contain a JSON object.`);
    }
    const obj = raw as Record<string, unknown>;

    for (const key of REQUIRED_RUNTIME_CONFIG_KEYS) {
      if (!(key in obj)) {
        errors.push(`missing required key "${key}"`);
      }
    }

    for (const key of ['apiUrl', 'apiUrlHub', 'apiVersion'] as const) {
      const value = obj[key];
      if (key in obj && (typeof value !== 'string' || value.length === 0)) {
        errors.push(`"${key}" must be a non-empty string (got ${typeof value})`);
      }
    }

    const devAuth = obj['devAuth'];
    if ('devAuth' in obj) {
      if (devAuth === null || typeof devAuth !== 'object' || Array.isArray(devAuth)) {
        errors.push('"devAuth" must be an object');
      } else if (typeof (devAuth as Record<string, unknown>)['publicKey'] !== 'string') {
        // Empty string IS allowed — matches environment.uat.ts /
        // environment.production.ts, which intentionally disable dev-login.
        errors.push('"devAuth.publicKey" must be a string (empty allowed)');
      }
    }

    if ('cacheDebug' in obj && typeof obj['cacheDebug'] !== 'boolean') {
      errors.push(`"cacheDebug" must be a boolean (got ${typeof obj['cacheDebug']})`);
    }
    if ('filingLoadRetryEnabled' in obj && typeof obj['filingLoadRetryEnabled'] !== 'boolean') {
      errors.push(`"filingLoadRetryEnabled" must be a boolean (got ${typeof obj['filingLoadRetryEnabled']})`);
    }

    if (
      'refactoredDiagnosticCodesEnabled' in obj &&
      typeof obj['refactoredDiagnosticCodesEnabled'] !== 'boolean'
    ) {
      errors.push(
        `"refactoredDiagnosticCodesEnabled" must be a boolean (got ${typeof obj['refactoredDiagnosticCodesEnabled']})`,
      );
    }

    if ('crossTabSyncDebug' in obj && typeof obj['crossTabSyncDebug'] !== 'boolean') {
      errors.push(`"crossTabSyncDebug" must be a boolean (got ${typeof obj['crossTabSyncDebug']})`);
    }

    if ('crossSheetCalcDebug' in obj && typeof obj['crossSheetCalcDebug'] !== 'boolean') {
      errors.push(`"crossSheetCalcDebug" must be a boolean (got ${typeof obj['crossSheetCalcDebug']})`);
    }

    if ('livenessProbeDebug' in obj && typeof obj['livenessProbeDebug'] !== 'boolean') {
      errors.push(`"livenessProbeDebug" must be a boolean (got ${typeof obj['livenessProbeDebug']})`);
    }

    if ('authAutocompleteEnabled' in obj && typeof obj['authAutocompleteEnabled'] !== 'boolean') {
      errors.push(`"authAutocompleteEnabled" must be a boolean (got ${typeof obj['authAutocompleteEnabled']})`);
    }

    if ('fieldNoteTooltipsEnabled' in obj && typeof obj['fieldNoteTooltipsEnabled'] !== 'boolean') {
      errors.push(`"fieldNoteTooltipsEnabled" must be a boolean (got ${typeof obj['fieldNoteTooltipsEnabled']})`);
    }

    if ('mandatoryMarkersEnabled' in obj && typeof obj['mandatoryMarkersEnabled'] !== 'boolean') {
      errors.push(`"mandatoryMarkersEnabled" must be a boolean (got ${typeof obj['mandatoryMarkersEnabled']})`);
    }

    if ('nonSuccessDownloadEnabled' in obj && typeof obj['nonSuccessDownloadEnabled'] !== 'boolean') {
      errors.push(`"nonSuccessDownloadEnabled" must be a boolean (got ${typeof obj['nonSuccessDownloadEnabled']})`);
    }

    if ('enginePickerEnabled' in obj && typeof obj['enginePickerEnabled'] !== 'boolean') {
      errors.push(`"enginePickerEnabled" must be a boolean (got ${typeof obj['enginePickerEnabled']})`);
    }

    if ('forceResetPasswordFailure' in obj) {
      if (typeof obj['forceResetPasswordFailure'] !== 'boolean') {
        errors.push(`"forceResetPasswordFailure" must be a boolean (got ${typeof obj['forceResetPasswordFailure']})`);
      } else if (obj['forceResetPasswordFailure'] && this.isProductionBuild) {
        // Disabled, not ignored: this flag would fail every real password reset.
        errors.push('"forceResetPasswordFailure" must be false in production builds');
      }
    }

    if ('simulateFailureEnabled' in obj) {
      if (typeof obj['simulateFailureEnabled'] !== 'boolean') {
        errors.push(`"simulateFailureEnabled" must be a boolean (got ${typeof obj['simulateFailureEnabled']})`);
      } else if (obj['simulateFailureEnabled'] && this.isProductionBuild) {
        // Disabled, not ignored: a stray true must surface, not silently no-op.
        errors.push('"simulateFailureEnabled" must be false in production builds');
      }
    }

    // A page size reaches the backend as a query param and sizes the
    // paginator, so a non-integer or zero would silently break both. Fail
    // the boot instead — the operator sees the bad key by name.
    const listingPageSize = obj['listingPageSize'];
    if ('listingPageSize' in obj) {
      if (listingPageSize === null || typeof listingPageSize !== 'object' || Array.isArray(listingPageSize)) {
        errors.push('"listingPageSize" must be an object');
      }
      else {
        const group = listingPageSize as Record<string, unknown>;
        for (const member of ['xbrlFiles', 'userManagement'] as const) {
          const value = group[member];
          if (typeof value !== 'number' || !Number.isInteger(value) || value < 1) {
            errors.push(
              `"listingPageSize.${member}" must be a positive integer (got ${JSON.stringify(value)})`,
            );
          }
        }
      }
    }

    if (errors.length > 0) {
      throw new Error(
        `RuntimeConfigService: ${source} failed schema validation:\n  - ${errors.join('\n  - ')}`,
      );
    }

    return raw as RuntimeConfig;
  }
}
